import { useState, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { getFilteredLogs, getMessageTypes } from "../../../../api/logsApi";
import { selectSessionToken } from "../../../../features/session/sessionSlice";
import { log, getStackTrace } from "@src/Log.init";

const useGetFilters = (
  setLogs,
  perPage,
  page,
  setTotal,
  setLoading,
  filters,
  setFilters,
  defaultFilters,
  ntAdministration
) => {
  const sessionToken = useSelector(selectSessionToken);

  const applications = [
    {
      label: "MIST API Server",
      value: "server",
    },
    {
      label: "Admin Portal",
      value: "portal",
    },
  ];

  const levels = [
    {
      label: "Trace",
      value: "trace",
    },
    {
      label: "Debug",
      value: "debug",
    },
    {
      label: "Info",
      value: "info",
    },
    {
      label: "Warn",
      value: "warn",
    },
    {
      label: "Error",
      value: "error",
    },
  ];

  const [locations, setLocations] = useState([]);
  const [workstations, setWorkstations] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [messages, setMessages] = useState([]);

  const handleClearFilters = () => {
    setFilters(defaultFilters);
  };

  const handleChange = (e) => {
    if (e.target.value.includes("all")) {
      setFilters((prev) => ({
        ...prev,
        ["messageTypes"]:
          filters["messageTypes"].length === messages.length
            ? []
            : messages.map((msg) => msg.value),
      }));
    } else {
      setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  const handleChangeCalendar = (calendar, name) => {
    setFilters((prev) => ({
      ...prev,
      [name]:
        typeof calendar.toISOString === "function"
          ? calendar.toISOString()
          : null,
    }));
  };

  const handleCheckboxChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked
        ? [...prev[e.target.name], e.target.value]
        : prev[e.target.name].filter((el) => el !== e.target.value),
    }));
  };

  const getCurrentApplication = () => {
    const applications = ["server", "portal"];
    if (
      typeof filters.activeApplication != "string" ||
      !applications.includes(filters.activeApplication)
    )
      return "server";

    if (filters.activeApplication != "server") return filters.activeApplication;

    return "server";
  };

  const handleSubmitForm = async () => {
    setLoading(true);

    await getFilteredLogs(sessionToken, {
      ...filters,
      activeApplication: getCurrentApplication(),
      perPage,
      page,
    })
      .then(async (response) => {
        await setLogs(response?.data);
        setTotal(response?.metadata[0]?.total);
      })
      .finally(() => {
        setLoading(false);
      })
      .catch((err) => {
        log.error({
          message_type: "SystemError",
          message: "Get filtered logs failed",
          meta_data: getStackTrace(),
        });
      });
  };

  useLayoutEffect(() => {
    if (filters !== defaultFilters) {
      handleSubmitForm();
    }

    return () => {};
  }, [page, perPage, filters.activeApplication]);

  useLayoutEffect(() => {
    if (
      filters.levelTypes.length > 0 &&
      filters.messageTypes.length > 0 &&
      filters.applicationTypes.length > 0
    ) {
      setEnabled(true);
    }
    return () => {
      setEnabled(false);
    };
  }, [
    filters.applicationTypes,
    filters.levelTypes,
    filters.messageTypes,
    filters.location,
    filters.workstation,
  ]);

  useLayoutEffect(() => {
    if (
      !filters.applicationTypes.includes("client") &&
      !filters.applicationTypes.includes("server")
    ) {
      setFilters((prev) => ({
        ...prev,
        ["workstation"]: defaultFilters.workstation,
        ["location"]: defaultFilters.location,
      }));
    }

    // Get message types
    if (filters.applicationTypes.length > 0) {
      setFilters((prev) => ({
        ...prev,
        ["messageTypes"]: defaultFilters.messageTypes,
      }));

      getMessageTypes(sessionToken, {
        applicationTypes: filters.applicationTypes,
        ntAdministration,
      })
        .then((response) => {
          setMessages(
            response.messageTypes.map((message) => {
              return {
                label: message,
                value: message,
              };
            })
          );
        })
        .catch((err) => {
          log.error({
            message_type: "SystemError",
            message: "Get message types failed",
            meta_data: getStackTrace(),
          });
        });
    }
  }, [filters.applicationTypes]);

  return {
    enabled,
    locations,
    workstations,
    applications,
    levels,
    messages,
    handleClearFilters,
    handleChange,
    handleCheckboxChange,
    handleChangeCalendar,
    handleSubmitForm,
  };
};

export default useGetFilters;
